import React from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../ui/Card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/Table";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { formatNumber, formatDecimal, formatPercent, formatDate, formatMonth } from "./format";
import { downloadSectionedXlsx } from "./xlsxExport";
import { KpiCard, TablePager, usePagedRows } from "./ui";

// Badge here only ever uses the "outline" variant AnalysisTab.js already
// relies on elsewhere on this page — color comes from className instead of a
// variant prop, the same way deltaTextClass drives color for deltas, so this
// doesn't depend on Badge supporting variants beyond the one already in use.
const RATING_TEXT_CLASS = {
  High: "text-green-600",
  Neutral: "text-muted-foreground",
  Low: "text-red-600",
};

function RatingBadge({ rating, bucket }) {
  return (
    <Badge variant="outline" className={RATING_TEXT_CLASS[bucket] || ""}>
      {rating ?? "—"}
      {bucket ? ` · ${bucket}` : ""}
    </Badge>
  );
}

// Rating 1-5 is an ORDINAL bucket, but the order is already fully carried by
// the bar's left-to-right position and the 1★–5★ axis label beneath it, so
// color doesn't need to encode order here. bg-primary turned out to render
// as fully invisible in this build (the theme's --primary CSS variable isn't
// resolving to a visible color in this component's context), even though the
// class name itself is used elsewhere — so this uses a concrete Tailwind
// color (bg-purple-500, matching the LTV heatmap's fill) that doesn't depend
// on that variable, guaranteeing the bar actually paints. Direct count label
// + axis label + hover title still carry the rest.
const CHART_HEIGHT_PX = 120;

function RatingDistributionChart({ distribution }) {
  const maxCount = Math.max(...distribution.map((d) => d.count), 1);
  return (
    <div className="flex items-end gap-3" style={{ height: CHART_HEIGHT_PX + 44 }}>
      {distribution.map((d) => {
        const barHeight = Math.max(Math.round((d.count / maxCount) * CHART_HEIGHT_PX), 3);
        return (
          <div
            key={d.rating}
            className="flex flex-col items-center gap-1 flex-1 min-w-[36px]"
            title={`${formatNumber(d.count)} feedback at ${d.rating} star${d.rating === 1 ? "" : "s"} (${d.bucket})`}
          >
            <span className="text-xs font-medium text-foreground">{formatNumber(d.count)}</span>
            <div
              className="w-full max-w-[40px] rounded-t bg-purple-500"
              style={{ height: `${barHeight}px` }}
            />
            <span className="text-xs text-muted-foreground">{d.rating}★</span>
          </div>
        );
      })}
    </div>
  );
}

// Demo-only summary of the in-memory Pylon ticket store — labeled clearly
// (an amber "Demo only" badge, plus the actual synced date window spelled
// out) so this never reads as a real historical metric. total_count here is
// the full matched population for the current filters, not the
// RETENTION_ROW_LIMIT-capped rows shown in the table below.
function PylonTicketSummary({ summary }) {
  const { total_count, distinct_provider_count, sync_window, by_category } = summary;
  return (
    <Card className="border-amber-300">
      <CardHeader>
        <div className="flex items-center gap-2 flex-wrap">
          <CardTitle className="text-base">Pylon support tickets</CardTitle>
          <Badge variant="outline" className="text-amber-600">
            Demo only
          </Badge>
        </div>
        <CardDescription>
          {sync_window
            ? `Synced from Pylon for ${formatDate(sync_window.start)} to ${formatDate(
                sync_window.end
              )} only — a short demo window, not the full ticket history. Click "Sync Pylon tickets" above to refresh.`
            : 'No tickets synced yet this server session — click "Sync Pylon tickets" above to pull a demo sample.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {total_count === 0 ? (
          <p className="text-sm text-muted-foreground">No matched tickets for the current filters.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-2xl font-semibold">{formatNumber(total_count)}</p>
              <p className="text-xs text-muted-foreground">Matched tickets (filtered)</p>
            </div>
            <div>
              <p className="text-2xl font-semibold">{formatNumber(distinct_provider_count)}</p>
              <p className="text-xs text-muted-foreground">Distinct users who submitted a ticket</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">By category</p>
              <ul className="space-y-0.5">
                {by_category.map((c) => (
                  <li key={c.category} className="text-sm flex justify-between gap-2">
                    <span className="text-foreground">{c.category}</span>
                    <span className="text-muted-foreground">{formatNumber(c.count)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// "Are they staying with us?" — quick summary at the top of the page,
// answering that question separately for the three groups the ticket/
// feedback data lets us identify: providers who left a good (high) rating,
// providers who left a low rating, and providers who filed a support
// ticket. "Staying" = currently_active from the backend, i.e. at least one
// recording in the trailing 30 days as of today — the same definition the
// LTV tab uses, not just activity around the time of the review/ticket.
function StayingSummary({ staying_summary }) {
  const groups = [
    { key: "high_rating", label: "Submitted a good (high) rating", data: staying_summary.high_rating },
    { key: "low_rating", label: "Submitted a low rating", data: staying_summary.low_rating },
    { key: "ticket", label: "Submitted a support ticket", data: staying_summary.ticket },
  ];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Are they staying with us?</CardTitle>
        <CardDescription>
          Share of each group still active today (a recording in the trailing 30 days), for the current filters.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {groups.map((g) => (
            <div key={g.key}>
              <p className="text-2xl font-semibold">
                {g.data.distinct_provider_count === 0 ? "—" : formatPercent(g.data.currently_active_pct)}
              </p>
              <p className="text-xs text-muted-foreground">{g.label}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {g.data.distinct_provider_count === 0
                  ? "No matching users for the current filters"
                  : `${formatNumber(g.data.currently_active_count)} of ${formatNumber(
                      g.data.distinct_provider_count
                    )} users still active`}
              </p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Month-over-month retention for the power-user cohort (weekly notes above
// power_user_retention.weekly_note_threshold for that calendar month,
// normalized to a 7-day rate rather than a live trailing-7-day snapshot —
// see the backend's power_user_retention.definition string, surfaced below
// as the card description, for the exact rule). Two measures per cohort
// month, side by side, since "retained" is ambiguous on its own:
// retained_as_power_user (stricter — still above the threshold the
// following month) and retained_as_active (broader — recorded anything at
// all the following month). A cohort month whose following month is still
// in progress is labeled as such so its numbers aren't read as final.
function PowerUserRetention({ power_user_retention }) {
  const { months, definition } = power_user_retention;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Power-user retention (month over month)</CardTitle>
        <CardDescription>{definition}</CardDescription>
      </CardHeader>
      <CardContent>
        {months.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Not enough monthly history yet for the current filters.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cohort month</TableHead>
                  <TableHead>Power users</TableHead>
                  <TableHead>Retained as power user</TableHead>
                  <TableHead>Retained as active</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {months.map((m) => (
                  <TableRow key={m.month}>
                    <TableCell className="align-top whitespace-nowrap">
                      {formatMonth(`${m.month}-01`)} → {formatMonth(`${m.next_month}-01`)}
                      {m.next_month_in_progress && (
                        <Badge variant="outline" className="ml-2 text-amber-600">
                          in progress
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="align-top whitespace-nowrap">{formatNumber(m.power_user_count)}</TableCell>
                    <TableCell className="align-top whitespace-nowrap">
                      {formatPercent(m.retained_as_power_user_pct)}{" "}
                      <span className="text-xs text-muted-foreground">
                        ({formatNumber(m.retained_as_power_user_count)} of {formatNumber(m.power_user_count)})
                      </span>
                    </TableCell>
                    <TableCell className="align-top whitespace-nowrap">
                      {formatPercent(m.retained_as_active_pct)}{" "}
                      <span className="text-xs text-muted-foreground">
                        ({formatNumber(m.retained_as_active_count)} of {formatNumber(m.power_user_count)})
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const EMPTY_FEEDBACK = [];

export default function RetentionTab({ data, loading, error, onSyncTickets, ticketSyncStatus, ticketSyncMessage }) {
  const feedback = data?.feedback || EMPTY_FEEDBACK;
  const pager = usePagedRows(feedback, { pageSize: 10 });

  if (loading && !data) {
    return <p className="text-sm text-muted-foreground">Loading Customer Retention report…</p>;
  }
  if (error) {
    return <p className="text-sm text-red-600">{error}</p>;
  }
  if (!data) return null;

  const {
    total_count,
    returned_count,
    row_limit,
    summary,
    rating_distribution,
    ticket_summary,
    staying_summary,
    power_user_retention,
  } = data;

  const handleDownloadXlsx = () => {
    const sections = [
      {
        title:
          total_count > returned_count
            ? `Feedback (showing most recent ${formatNumber(returned_count)} of ${formatNumber(
                total_count
              )}, capped at ${formatNumber(row_limit)} — narrow filters for older rows)`
            : "Feedback",
        columns: [
          { header: "Date", key: "appointment_date" },
          { header: "Source", key: "source" },
          { header: "User", key: "provider_name" },
          { header: "Site", key: "site_name" },
          { header: "Station ID", key: "station_id" },
          { header: "Specialty", key: "specialty" },
          { header: "Rating", key: "star_rating" },
          { header: "Rating Bucket", key: "rating_bucket" },
          { header: "Feedback", key: "feedback_comments" },
          { header: "Same-day Recordings", key: "same_day_recordings" },
          { header: "30d Recordings", key: "recordings_30d" },
          { header: "30d Active Days", key: "active_days_30d" },
        ],
        rows: feedback,
      },
      {
        title: "Summary",
        columns: [
          { header: "Total Feedback", key: "total_count" },
          { header: "Distinct Users", key: "distinct_provider_count" },
          { header: "Avg Rating", key: "avg_rating" },
          { header: "High Count", key: "high_count" },
          { header: "High %", key: "high_pct", format: "percent" },
          { header: "High Distinct Users", key: "high_distinct_provider_count" },
          { header: "Low Count", key: "low_count" },
          { header: "Low %", key: "low_pct", format: "percent" },
          { header: "Low Distinct Users", key: "low_distinct_provider_count" },
        ],
        rows: [summary],
      },
      {
        title: "Rating Distribution",
        columns: [
          { header: "Rating", key: "rating" },
          { header: "Bucket", key: "bucket" },
          { header: "Count", key: "count" },
        ],
        rows: rating_distribution,
      },
    ];
    if (staying_summary) {
      sections.push({
        title: "Staying With Us",
        columns: [
          { header: "Group", key: "group" },
          { header: "Distinct Users", key: "distinct_provider_count" },
          { header: "Currently Active Count", key: "currently_active_count" },
          { header: "Currently Active %", key: "currently_active_pct", format: "percent" },
        ],
        rows: [
          { group: "Submitted a good (high) rating", ...staying_summary.high_rating },
          { group: "Submitted a low rating", ...staying_summary.low_rating },
          { group: "Submitted a support ticket", ...staying_summary.ticket },
        ],
      });
    }
    if (power_user_retention) {
      sections.push({
        title: "Power User Retention (MoM)",
        columns: [
          { header: "Cohort Month", key: "month" },
          { header: "Next Month", key: "next_month" },
          { header: "Next Month In Progress", key: "next_month_in_progress" },
          { header: "Power Users", key: "power_user_count" },
          { header: "Retained As Power User", key: "retained_as_power_user_count" },
          { header: "Retained As Power User %", key: "retained_as_power_user_pct", format: "percent" },
          { header: "Retained As Active", key: "retained_as_active_count" },
          { header: "Retained As Active %", key: "retained_as_active_pct", format: "percent" },
        ],
        rows: power_user_retention.months,
      });
    }
    if (ticket_summary) {
      sections.push({
        title: "Ticket Summary",
        columns: [
          { header: "Matched Tickets", key: "total_count" },
          { header: "Distinct Users", key: "distinct_provider_count" },
          { header: "Sync Window Start", key: "sync_start" },
          { header: "Sync Window End", key: "sync_end" },
        ],
        rows: [
          {
            total_count: ticket_summary.total_count,
            distinct_provider_count: ticket_summary.distinct_provider_count,
            sync_start: ticket_summary.sync_window ? ticket_summary.sync_window.start : "",
            sync_end: ticket_summary.sync_window ? ticket_summary.sync_window.end : "",
          },
        ],
      });
      sections.push({
        title: "Tickets by Category",
        columns: [
          { header: "Category", key: "category" },
          { header: "Count", key: "count" },
        ],
        rows: ticket_summary.by_category,
      });
    }
    downloadSectionedXlsx("retention.xlsx", "Retention", sections);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold">Customer Retention (beta)</h2>
          <p className="text-sm text-muted-foreground">
            Feedback and matched Pylon tickets for the users in the current filters, with how active each person
            was at the time. Same-day recordings, plus recordings and active days in the 30 days before that date.
            Tickets match by email and show in the list as Source = Ticket. Set a report month to limit both sources
            to that month. High is 4–5 stars and Low is 1–2 stars; those buckets apply to feedback only.
          </p>
          {total_count > returned_count && (
            <p className="text-xs text-muted-foreground mt-1">
              Showing the most recent {formatNumber(returned_count)} of {formatNumber(total_count)} matching rows
              (capped at {formatNumber(row_limit)}). Narrow the filters to see older entries.
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleDownloadXlsx}>
              Download XLSX
            </Button>
            {onSyncTickets && (
              <Button
                type="button"
                variant="outline"
                onClick={(e) => {
                  e.preventDefault();
                  onSyncTickets();
                }}
                disabled={ticketSyncStatus === "syncing"}
              >
                {ticketSyncStatus === "syncing" ? "Syncing…" : "Sync Pylon tickets"}
              </Button>
            )}
          </div>
          {ticketSyncMessage && (
            <p className={`text-xs max-w-xs text-right ${ticketSyncStatus === "error" ? "text-red-600" : "text-muted-foreground"}`}>
              {ticketSyncMessage}
            </p>
          )}
        </div>
      </div>

      {staying_summary && <StayingSummary staying_summary={staying_summary} />}

      {power_user_retention && <PowerUserRetention power_user_retention={power_user_retention} />}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          label="Feedback (filtered)"
          value={formatNumber(summary.total_count)}
          sublabel={`${formatNumber(summary.distinct_provider_count)} distinct users`}
        />
        <KpiCard label="Avg rating" value={formatDecimal(summary.avg_rating, 2)} sublabel="out of 5" />
        <KpiCard
          label="High ratings"
          value={formatNumber(summary.high_count)}
          sublabel={`${formatPercent(summary.high_pct)} of feedback · ${formatNumber(
            summary.high_distinct_provider_count
          )} distinct users`}
        />
        <KpiCard
          label="Low ratings"
          value={formatNumber(summary.low_count)}
          sublabel={`${formatPercent(summary.low_pct)} of feedback · ${formatNumber(
            summary.low_distinct_provider_count
          )} distinct users`}
        />
      </div>

      {ticket_summary && <PylonTicketSummary summary={ticket_summary} />}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Rating distribution</CardTitle>
          <CardDescription>
            Count of feedback at each star rating, for the current filters ({formatNumber(summary.total_count)}{" "}
            total)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {rating_distribution.length === 0 ? (
            <p className="text-sm text-muted-foreground">No feedback matches the selected filters.</p>
          ) : (
            <RatingDistributionChart distribution={rating_distribution} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Feedback</CardTitle>
          <CardDescription>
            Newest first. Showing up to {formatNumber(row_limit)} of the latest matching rows.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {feedback.length === 0 ? (
            <p className="text-sm text-muted-foreground">No feedback matches the selected filters.</p>
          ) : (
            /* Natural (auto) table layout, not a forced w-full/table-fixed —
               columns size to their content and the table itself shrinks
               with the viewport; overflow-x-auto is what handles the case
               where it's still wider than the card, not a fixed width. */
            <>
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Site</TableHead>
                  <TableHead>Specialty</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>Feedback</TableHead>
                  <TableHead>Same-day</TableHead>
                  <TableHead>30d recordings</TableHead>
                  <TableHead>30d active days</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pager.pageRows.map((row) => (
                  <TableRow key={row.appointment_id}>
                    <TableCell className="align-top whitespace-nowrap">{formatDate(row.appointment_date)}</TableCell>
                    <TableCell className="align-top whitespace-nowrap">
                      <Badge variant="outline" className={row.source === "ticket" ? "text-purple-600" : "text-muted-foreground"}>
                        {row.source === "ticket" ? "Ticket" : "Feedback"}
                      </Badge>
                    </TableCell>
                    <TableCell className="align-top whitespace-nowrap">{row.provider_name || "—"}</TableCell>
                    <TableCell className="align-top whitespace-nowrap">
                      {row.site_name ? `${row.site_name} (${row.station_id})` : row.station_id || "—"}
                    </TableCell>
                    <TableCell className="align-top whitespace-nowrap">{row.specialty || "—"}</TableCell>
                    <TableCell className="align-top whitespace-nowrap">
                      <RatingBadge rating={row.star_rating} bucket={row.rating_bucket} />
                    </TableCell>
                    {/* Feedback is abbreviated to a single line (truncate =
                        overflow-hidden + ellipsis + nowrap) so one long
                        comment doesn't blow out the row height; max-w-sm
                        gives the ellipsis a width to trigger against. The
                        title attr surfaces the full text on hover. */}
                    <TableCell className="align-top truncate max-w-sm" title={row.feedback_comments || ""}>
                      {row.feedback_comments || "—"}
                    </TableCell>
                    <TableCell className="align-top whitespace-nowrap">{formatNumber(row.same_day_recordings)}</TableCell>
                    <TableCell className="align-top whitespace-nowrap">{formatNumber(row.recordings_30d)}</TableCell>
                    <TableCell className="align-top whitespace-nowrap">{formatNumber(row.active_days_30d)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
            <TablePager page={pager.page} totalPages={pager.totalPages} onPageChange={pager.setPage} />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
